/**
 * @file Web Gallery cart related actions.
 */
import get from 'lodash.get';

import { allowsPrintWatermarkOption } from '../../../../util/album';
import getLogger from '../../../../util/logger';
import {
  prepareCartForMutation,
  getDownloadStorePhotoItem,
} from '../../utilities/web-gallery-cart-utils';
import { getErrorString } from '../../../../util/normalize-errors';
import {
  ANALYTIC_EVENTS,
  trackWebGalleryEvent,
} from '../../utilities/analytics';
import { CART_ITEM_TYPES } from '../../constants/web-gallery-cart-constants';

const log = getLogger('PurchaseDownloadsPrintsAction');

export const SELECT_DOWNLOADS = 'purchase-downloads-prints/SELECT_DOWNLOADS';
export const DESELECT_DOWNLOAD = 'purchase-downloads-prints/DESELECT_DOWNLOAD';
export const TOGGLE_WATERMARK = 'purchase-downloads-prints/TOGGLE_WATERMARK';
export const SELECT_PRINT = 'purchase-downloads-prints/SELECT_PRINT';
export const DESELECT_PRINT = 'purchase-downloads-prints/DESELECT_PRINT';
// Makes no sense to have download quantity
export const SET_PRINT_QUANTITY =
  'purchase-downloads-prints/SET_PRINT_QUANTITY';
export const CHECKOUT_REQUEST = 'purchase-downloads-prints/CHECKOUT_REQUEST';
export const CHECKOUT_SUCCESS = 'purchase-downloads-prints/CHECKOUT_SUCCESS';
export const CHECKOUT_FAILURE = 'purchase-downloads-prints/CHECKOUT_FAILURE';
export const RESET_DOWNLOADS = 'purchase-downloads-prints/RESET_DOWNLOADS';
export const RESET_PRINTS = 'purchase-downloads-prints/RESET_PRINTS';
export const WEB_GALLERY_UPSELL_SEEN =
  'purchase-downloads-prints/WEB_GALLERY_UPSELL_SEEN';

export const markUpsellSeen = () => {
  return { type: WEB_GALLERY_UPSELL_SEEN };
};

export const selectDownloads = items => {
  return {
    type: SELECT_DOWNLOADS,
    items: items.map(item =>
      getDownloadStorePhotoItem(item.photoItem, item.album),
    ),
  };
};

export const selectDownload = (photoItem, album) => {
  trackWebGalleryEvent(ANALYTIC_EVENTS.CART_DID_INCREASE_QUANTITY, {
    item_type: CART_ITEM_TYPES.DOWNLOAD,
    photo_id: get(photoItem, 'id'),
    album_id: get(album, 'id'),
  });

  return selectDownloads([{ photoItem, album }]);
};

export const deselectDownload = (photoItem, album) => {
  trackWebGalleryEvent(ANALYTIC_EVENTS.CART_DID_REMOVE_ITEM, {
    item_type: CART_ITEM_TYPES.DOWNLOAD,
    photo_id: get(photoItem, 'id'),
    album_id: get(album, 'id'),
  });

  return { type: DESELECT_DOWNLOAD, photoItem, album };
};

export const selectPrint = (photoItem, album, printSize, quantity = 1) => {
  trackWebGalleryEvent(ANALYTIC_EVENTS.CART_DID_INCREASE_QUANTITY, {
    item_type: CART_ITEM_TYPES.PRINT,
    photo_id: get(photoItem, 'id'),
    album_id: get(album, 'id'),
    print_size: get(printSize, 'name'),
  });

  return {
    type: SELECT_PRINT,
    photoItem,
    printSize,
    quantity,
    priceInfo: { ...printSize.price },
    album: {
      id: album.id,
      name: album.name,
      allowsWatermarkOption: allowsPrintWatermarkOption(album),
    },
  };
};

export const deselectPrint = (photoItem, album, printSize) => {
  trackWebGalleryEvent(ANALYTIC_EVENTS.CART_DID_REMOVE_ITEM, {
    item_type: CART_ITEM_TYPES.PRINT,
    photo_id: get(photoItem, 'id'),
    album_id: get(album, 'id'),
    print_size: get(printSize, 'name'),
  });

  return {
    type: DESELECT_PRINT,
    photoItem,
    album,
    printSize,
  };
};

export const toggleWatermarkOption = (photoItem, album, printSize) => {
  return {
    type: TOGGLE_WATERMARK,
    photoItem,
    album,
    printSize,
  };
};

export const resetDownloads = () => {
  return { type: RESET_DOWNLOADS };
};

export const resetPrints = () => {
  return { type: RESET_PRINTS };
};

export const setPrintQuantity = (photoItem, album, printSize, quantity) => {
  return {
    type: SET_PRINT_QUANTITY,
    quantity,
    photoItem,
    printSize,
    album,
  };
};

const checkoutRequest = () => {
  return { type: CHECKOUT_REQUEST };
};

const checkoutSuccess = () => {
  return { type: CHECKOUT_SUCCESS };
};

const checkoutFailure = error => {
  return { type: CHECKOUT_FAILURE, error };
};

export const onCheckout = async (
  accountId,
  emailAddress,
  stripeToken,
  selectedDownloads,
  selectedPrints,
  addressId,
  dispatch,
  checkoutMutation,
  stripePaymentMethodId,
  stripeConnectAccountId,
) => {
  log.info('checkout() :: Purchasing items: ', [
    ...selectedDownloads,
    ...selectedPrints,
  ]);

  dispatch(checkoutRequest());

  try {
    const purchaseData = prepareCartForMutation(
      accountId,
      addressId,
      selectedDownloads,
      selectedPrints,
      stripeToken,
      emailAddress,
      stripePaymentMethodId,
      stripeConnectAccountId,
    );

    const checkoutResponse = await checkoutMutation(purchaseData);

    log.info('checkout() :: Completed with data: ', checkoutResponse);
    dispatch(checkoutSuccess());

    trackWebGalleryEvent(ANALYTIC_EVENTS.CART_DID_SUBMIT_ORDER, {
      prints_quantity: selectedPrints.length,
      downloads_quantity: selectedDownloads.length,
      total_amount: selectedPrints.length + selectedDownloads.length,
    });
  } catch (error) {
    log.error('checkout() :: Error ', error);
    dispatch(checkoutFailure(getErrorString(error)));
  }
};
